/**
 * ManageCrew.jsx — Crew Management & Permissions
 * Browning Electric / ElecBid
 *
 * Accessible only to users with manage_crew permission (owner role).
 * - List all crew members with role, email, active status
 * - Invite new members with role + granular permission assignment
 * - Edit permissions for existing members inline
 * - Deactivate / reactivate members
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { usePermissions } from "../hooks/usePermissions";
import { useSession } from "../App";

// ── Brand colours ────────────────────────────────────────────────────────────
const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a3140",
  accent: "#a5ce3a", accentDim: "rgba(165,206,58,0.12)",
  green: "#22c55e", greenDim: "rgba(34,197,94,0.10)",
  red: "#ef4444", redDim: "rgba(239,68,68,0.10)",
  yellow: "#eab308", yellowDim: "rgba(234,179,8,0.10)",
  blue: "#3b82f6", blueDim: "rgba(59,130,246,0.10)",
  text: "#f0f4f8", muted: "#8892a4", dim: "#4a5568",
};

// ── Jobber employee names — hardcoded *fallback* ─────────────────────────────
// The live dropdown is built at runtime from the synced `jobber_users` table
// (see the `jobberEmployees` useMemo in the main component), so anyone added in
// Jobber shows up as a link option automatically — no deploy needed. This
// fallback only seeds the roster order and covers the first render before
// jobber_users loads. Mirrors BonusTracker.ACTIVE_EMPLOYEES_FALLBACK.
const JOBBER_EMPLOYEES_FALLBACK = [
  "Dylan", "Colton", "Kyle", "Landon", "Morgan",
  "Jon", "Justin", "Taylor", "Tommy", "Ayden", "Cindy",
];

// First name, lowercased — the key we de-dupe Jobber employees by (mirrors
// BonusTracker.firstName).
function firstName(name) {
  if (!name) return "";
  return name.trim().split(/\s+/)[0].toLowerCase();
}

// ── Tab-level permission definitions ─────────────────────────────────────────
// Each entry maps to one tab in ElecBid's nav.
// alwaysOn   → always visible to all users, no toggle shown
// ownerOnly  → only the owner can access, shown as locked
// adminOnly  → admin/owner only, shown as locked
// viewKey    → permission key that controls tab visibility
// editKey    → if set, show a Read / Read+Edit dropdown when tab is on
// sharedNote → shown when this tab shares its permission with another
// subItems   → granular sub-permissions shown when the tab is toggled on
const TAB_PERMS = [
  { tab: "Requests",           alwaysOn: true,
    description: "Job requests, client contact info and scheduling — the landing tab every crew member gets." },
  // /schedule is tier public (every crew member sees their own day, adds notes,
  // clocks in/out) — but DISPATCH (booking/moving visits, creating/editing
  // tasks, seeing everyone's tasks) is its own grant. Admins always pass.
  { tab: "Schedule",           alwaysOn: true,
    description: "The crew schedule: their visits + tasks, notes with photos, clock in/out. Every crew member gets it.",
    subItems: [
      { key: "edit_schedule", label: "Dispatch — book & move visits, create/edit tasks",
        description: "Schedule writes: book and reschedule visits, reassign techs, create/edit/delete calendar tasks, and see the whole crew's tasks. For whoever runs the calendar (Taylor). Changes push straight into Jobber." },
    ],
  },
  // Gated 2026-07-22 (was always-on): quotes carry pricing, labour rates and
  // margins. Route + nav enforced via view_quotes in src/config/permissions.js.
  { tab: "Quotes",             viewKey: "view_quotes",
    description: "The Quotes tab and open quote drafts — pricing, labour rates and margins are all visible there. Leave OFF for apprentices." },
  { tab: "Price List",         alwaysOn: true,
    description: "Supplier price book — material costs and sell prices. Techs need it in the field." },
  { tab: "Calculators",        viewKey: "view_calculators",
    description: "CEC load calc, wire sizing and the other calculators. No business data." },
  {
    tab: "Crew",
    viewKey: "view_crew_metrics",
    description: "Crew Performance dashboard: hours, visit + note discipline metrics. The money and leaderboard views below are each their own toggle.",
    subItems: [
      { key: "view_revenue_per_tech",   label: "Revenue Per Tech",
        description: "Billed revenue $ per tech on the dashboard cards — real dollar figures." },
      { key: "view_profit_per_tech",    label: "Profit Per Tech",
        description: "Job profit $ and margin per tech on the dashboard cards — real dollar figures." },
      { key: "view_job_table",          label: "Job Table",
        description: "Adds the Individual Details tab — a per-job breakdown table for each tech." },
      { key: "view_profitability",      label: "Job Profitability Leaderboard",
        description: "The ranked leaderboards: names, rankings, avg profit % and job counts for the whole crew. Bonus $ shows only on their OWN row unless the next toggle is on." },
      { key: "view_all_bonus_amounts",  label: "See Other Crew's Bonus $ on Leaderboard",
        description: "Reveals everyone's bonus dollar amounts on the leaderboards + the quarterly roll-up. OFF = they see who's in the lead, not what anyone makes." },
      { key: "edit_review_assignments", label: "Edit Review Assignments",
        description: "Can reassign Google-review credit from one tech to another." },
      { key: "view_master_crew_report", label: "Master Crew Report (cross-tech filter view)",
        description: "Adds the Master Report tab — every metric, filterable across all techs." },
    ],
  },
  // Jobs tab uses its own column-level perms (column_perms table) plus this
  // top-level visibility flag. Defaults to view_crew_metrics for legacy rows
  // (see Layout.jsx fallback) but owners can now flip it independently.
  // edit_job_billing is enforced on the SERVER (api/_billNotes.js) against the
  // person who typed the note in Jobber, matched by email. Owner and admins
  // always pass; this is how anyone else is let in.
  { tab: "Jobs",               viewKey: "view_jobs",
    description: "Jobs list + per-job detail: revenue, invoice status, visits and expenses for every job.",
    subItems: [
      { key: "edit_job_billing", label: "Bill a job from a Jobber note (/bill)",
        description: "Lets this person type /bill in a note on a T&M job to add its hours and materials to the job as a dated block of priced lines, without closing the job. Their Jobber login must use the same email as here. Leave OFF for field crew unless they do the billing." },
    ],
  },
  { tab: "Bonus Tracker",      viewKey: "view_bonus",        editKey: "edit_bonus", sharedNote: "sub-tab inside Crew page",
    description: "Their OWN bonus row only — scoped to the Jobber employee mapping above; they never see other crew's bonuses here. Admins see everyone. (Adjusting bonuses stays admin-only regardless of Read+Edit.)" },
  { tab: "Expenses",           viewKey: "view_expenses",
    description: "Invoice intelligence + the expense feed — supplier bills and dollar amounts." },
  { tab: "Reconciliation",     viewKey: "view_expenses",     sharedNote: "shares Expenses access",
    description: "Supplier statement reconciliation. Same permission as Expenses." },
  // Assessments are core to the request → assess → quote flow and currently
  // not route-gated. Mark as always-on so the toggle isn't misleading.
  { tab: "Assessments",        alwaysOn: true,
    description: "Smart Assessment flow on requests — scoping notes, photos, site details." },
  { tab: "Feedback",           alwaysOn: true,
    description: "Feedback hub. Also the landing page for accounts with no other tabs." },
  { tab: "QB Audit",           adminOnly: true,
    description: "Compares AP emails to QuickBooks bills." },
  { tab: "Coach",              adminOnly: true,
    description: "Financial coaching: monthly variance, balance sheet, budget." },
  { tab: "Marketing",          adminOnly: true,
    description: "Ad spend, leads and ROAS — the marketing command center." },
  { tab: "Job Auto-Fill",      adminOnly: true,
    description: "Pushes crew metrics into Jobber custom fields." },
  { tab: "Manage Crew",        ownerOnly: true,
    description: "This page — invites, roles and permissions." },
  { tab: "Jai-Man Learning",   adminOnly: true,
    description: "What Jai-Man remembers and has learned." },
  { tab: "Admin Diagnostics",  adminOnly: true,
    description: "Sync controls and service diagnostics." },
];

// ── Jai-Man permissions ──────────────────────────────────────────────────────
// Standalone toggles that don't map to a tab. Currently just one — financial
// data access through Jai-Man's tools (query_pnl, search_expenses, etc.).
const JAIMAN_PERMS = [
  {
    key: "view_jaiman_financials",
    label: "Financial data access",
    description: "Lets Jai-Man pull P&L, gas/vehicle spend, marketing spend, job profit, supplier spend, and the expense pipeline for this user. Off by default for crew.",
  },
];

const ALL_PERMISSION_KEYS = [...new Set([
  ...TAB_PERMS.flatMap(t => [
    t.viewKey,
    t.editKey,
    ...(t.subItems || []).map(s => s.key),
  ].filter(Boolean)),
  ...JAIMAN_PERMS.map(p => p.key),
])];

function allTrue() {
  return Object.fromEntries(ALL_PERMISSION_KEYS.map(k => [k, true]));
}
function allFalse() {
  return Object.fromEntries(ALL_PERMISSION_KEYS.map(k => [k, false]));
}

// ── Role badge ───────────────────────────────────────────────────────────────
function RoleBadge({ role }) {
  const styles = {
    owner: { bg: C.accentDim, color: C.accent },
    admin: { bg: C.blueDim, color: C.blue },
    crew:  { bg: `${C.border}40`, color: C.muted },
  };
  const s = styles[role] || styles.crew;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
      textTransform: "uppercase", padding: "2px 8px", borderRadius: 4,
      background: s.bg, color: s.color,
    }}>
      {role}
    </span>
  );
}

// ── Tab-based permission grid ─────────────────────────────────────────────────
function PermissionGrid({ value, onChange, disabled }) {
  const statusBadge = (label, color, bg) => (
    <span style={{
      fontSize: 10, fontWeight: 700, color, textTransform: "uppercase",
      letterSpacing: "0.06em", background: bg, padding: "2px 8px", borderRadius: 4,
    }}>{label}</span>
  );

  // Label + plain-English explanation of what the toggle actually grants —
  // rendered on every row so the owner never has to guess.
  const labelWithDesc = (t, labelColor) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <span style={{ fontSize: 13, color: labelColor }}>
        {t.tab}
        {t.sharedNote && (
          <span style={{ fontSize: 11, color: C.dim, marginLeft: 6 }}>({t.sharedNote})</span>
        )}
      </span>
      {t.description && (
        <div style={{ fontSize: 11, color: C.dim, marginTop: 2, lineHeight: 1.4 }}>
          {t.description}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 4 }}>
      {TAB_PERMS.map(t => {
        // ── Always-visible tabs ──
        if (t.alwaysOn) {
          return (
            <div key={t.tab}>
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "7px 10px", borderRadius: 7, opacity: 0.55,
              }}>
                {labelWithDesc(t, C.text)}
                {statusBadge("Always visible", C.green, `${C.green}18`)}
              </div>
              {/* Always-on tabs can still carry grantable sub-permissions
                  (e.g. Schedule → dispatch). Same block togglable tabs use. */}
              {t.subItems && (
                <div style={{ marginLeft: 28, paddingTop: 2, paddingBottom: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                  {t.subItems.map(sub => (
                    <label key={sub.key} style={{
                      display: "flex", alignItems: "flex-start", gap: 8, padding: "4px 10px",
                      borderRadius: 6, cursor: disabled ? "default" : "pointer",
                    }}>
                      <input
                        type="checkbox"
                        checked={!!value[sub.key]}
                        disabled={disabled}
                        onChange={e => {
                          if (disabled) return;
                          onChange({ ...value, [sub.key]: e.target.checked });
                        }}
                        style={{ accentColor: C.accent, width: 13, height: 13, marginTop: 2 }}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 12, color: C.muted }}>{sub.label}</span>
                        {sub.description && (
                          <span style={{ display: "block", fontSize: 11, color: C.dim, marginTop: 1, lineHeight: 1.4 }}>
                            {sub.description}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        }

        // ── Owner-only tabs ──
        if (t.ownerOnly) {
          return (
            <div key={t.tab} style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              padding: "7px 10px", borderRadius: 7, opacity: 0.5,
            }}>
              {labelWithDesc(t, C.text)}
              {statusBadge("Owner only", C.accent, C.accentDim)}
            </div>
          );
        }

        // ── Admin-only tabs ──
        if (t.adminOnly) {
          return (
            <div key={t.tab} style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              padding: "7px 10px", borderRadius: 7, opacity: 0.5,
            }}>
              {labelWithDesc(t, C.text)}
              {statusBadge("Admin only", C.blue, C.blueDim)}
            </div>
          );
        }

        // ── Togglable tabs ──
        const isOn = !!value[t.viewKey];
        const canEdit = !!t.editKey;
        const isEditing = canEdit && !!value[t.editKey];

        return (
          <div key={t.tab}>
            <div style={{
              display: "flex", alignItems: "flex-start", gap: 10, padding: "7px 10px",
              borderRadius: 7,
              background: isOn ? `${C.accent}08` : "transparent",
              border: `1px solid ${isOn ? C.accent + "30" : "transparent"}`,
            }}>
              <input
                type="checkbox"
                checked={isOn}
                disabled={disabled}
                onChange={e => {
                  if (disabled) return;
                  const next = { ...value, [t.viewKey]: e.target.checked };
                  if (!e.target.checked && t.editKey) next[t.editKey] = false;
                  onChange(next);
                }}
                style={{ accentColor: C.accent, width: 14, height: 14, marginTop: 2, cursor: disabled ? "default" : "pointer" }}
              />
              {labelWithDesc(t, isOn ? C.text : C.muted)}
              {isOn && canEdit && (
                <select
                  value={isEditing ? "edit" : "read"}
                  disabled={disabled}
                  onChange={e => {
                    if (disabled) return;
                    onChange({ ...value, [t.viewKey]: true, [t.editKey]: e.target.value === "edit" });
                  }}
                  style={{
                    padding: "3px 8px", borderRadius: 6,
                    border: `1px solid ${C.border}`, background: C.card,
                    color: C.text, fontSize: 12,
                    cursor: disabled ? "default" : "pointer",
                  }}
                >
                  <option value="read">Read</option>
                  <option value="edit">Read + Edit</option>
                </select>
              )}
            </div>

            {/* Sub-permissions shown indented when tab is on */}
            {isOn && t.subItems && (
              <div style={{ marginLeft: 28, paddingTop: 2, paddingBottom: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                {t.subItems.map(sub => (
                  <label key={sub.key} style={{
                    display: "flex", alignItems: "flex-start", gap: 8, padding: "4px 10px",
                    borderRadius: 6, cursor: disabled ? "default" : "pointer",
                  }}>
                    <input
                      type="checkbox"
                      checked={!!value[sub.key]}
                      disabled={disabled}
                      onChange={e => {
                        if (disabled) return;
                        onChange({ ...value, [sub.key]: e.target.checked });
                      }}
                      style={{ accentColor: C.accent, width: 13, height: 13, marginTop: 2 }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 12, color: C.muted }}>{sub.label}</span>
                      {sub.description && (
                        <span style={{ display: "block", fontSize: 11, color: C.dim, marginTop: 1, lineHeight: 1.4 }}>
                          {sub.description}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* ── Jai-Man Permissions ────────────────────────────────────────── */}
      <div style={{
        marginTop: 14, paddingTop: 10,
        borderTop: `1px solid ${C.border}`,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
          textTransform: "uppercase", color: C.muted, padding: "0 10px 6px",
        }}>
          Jai-Man Permissions
        </div>
        {JAIMAN_PERMS.map(p => {
          const isOn = !!value[p.key];
          return (
            <div key={p.key} style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              padding: "8px 10px", borderRadius: 7,
              background: isOn ? `${C.accent}08` : "transparent",
              border: `1px solid ${isOn ? C.accent + "30" : "transparent"}`,
            }}>
              <input
                type="checkbox"
                checked={isOn}
                disabled={disabled}
                onChange={e => {
                  if (disabled) return;
                  onChange({ ...value, [p.key]: e.target.checked });
                }}
                style={{ accentColor: C.accent, width: 14, height: 14, marginTop: 2, cursor: disabled ? "default" : "pointer" }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: isOn ? C.text : C.muted }}>{p.label}</div>
                {p.description && (
                  <div style={{ fontSize: 11, color: C.dim, marginTop: 2, lineHeight: 1.4 }}>
                    {p.description}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Invite form ──────────────────────────────────────────────────────────────
function InviteForm({ inviterEmail, authToken, existingMappings, allMembers, jobberEmployees, jobberUsers, onSaved, onCancel }) {
  const [inviteType, setInviteType] = useState("google"); // "google" | "email"
  const [linkedMemberId, setLinkedMemberId] = useState(""); // "" = create new; ID = link existing
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState("crew");
  const [jobberEmployee, setJobberEmployee] = useState("");
  const [permissions, setPermissions] = useState(allFalse());
  // Untick to add the member WITHOUT emailing them — lets the owner View As
  // them to check permissions first, then send from the row when happy.
  const [sendInviteNow, setSendInviteNow] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [manualLink, setManualLink] = useState(null);
  const [manualLinkCopied, setManualLinkCopied] = useState(false);

  const isLinkingExisting = inviteType === "email" && !!linkedMemberId;

  function handleRoleChange(newRole) {
    setRole(newRole);
    setPermissions(newRole === "admin" ? allTrue() : allFalse());
  }

  function handleInviteTypeChange(type) {
    setInviteType(type);
    setLinkedMemberId("");
    setError(null);
    setManualLink(null);
  }

  // Selecting a Jobber employee auto-fills email + display name from the synced
  // jobber_users row (matched on first name — the same key the mapping uses).
  // Fields stay editable; fallback-roster names with no synced row fill nothing.
  // Shared/agency logins ("Taylor & Mallory", taylor@nabocreative.com) are not
  // people — never fill from them; on first-name collisions (3 Taylors!) prefer
  // a @browning-electric.ca address. Mirrors quote-convert.js mapAssignee.
  function handleJobberEmployeeChange(name) {
    setJobberEmployee(name);
    if (!name) return;
    const cands = (jobberUsers || []).filter(
      u => u?.is_active !== false &&
           firstName(u?.name) === firstName(name) &&
           !(u?.name || "").includes("&") &&
           !/nabocreative/i.test(u?.email || "")
    );
    const match = cands.find(u => /@browning-electric\.ca$/i.test(u?.email || "")) || cands[0];
    if (match?.email) setEmail(match.email.toLowerCase());
    if (match?.name) setDisplayName(match.name);
  }

  async function sendApiInvite(toEmail, toName, toRole, crewMemberId) {
    const resp = await fetch("/api/send-invite", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { "Authorization": `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({
        email: toEmail,
        displayName: toName,
        role: toRole,
        inviterEmail,
        inviteType,
        crewMemberId: crewMemberId || null,
      }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(data?.error || `Invite API failed (${resp.status})`);
    }
    return data;
  }

  async function handleSave() {
    setError(null);
    const trimEmail = email.trim().toLowerCase();
    if (!trimEmail || !trimEmail.includes("@")) {
      setError("Enter a valid email address.");
      return;
    }

    // ── Email invite linking to an existing crew member ──────────────────────
    if (isLinkingExisting) {
      setSaving(true);
      try {
        const result = await sendApiInvite(trimEmail, null, null, linkedMemberId);
        if (result?.emailSent === false && result?.inviteLink) {
          setManualLink(result.inviteLink);
          setError(`Email send failed: ${result.emailError || "unknown error"}. Copy the link below and send it manually.`);
        } else {
          onSaved("email");
        }
      } catch (err) {
        setError(err.message || "Failed to send invite.");
      } finally {
        setSaving(false);
      }
      return;
    }

    // ── Create new crew member (Google or Email invite) ──────────────────────
    const trimName = displayName.trim();
    if (!trimName) {
      setError("Display name is required.");
      return;
    }
    setSaving(true);
    try {
      const perms = (role === "owner" || role === "admin") ? allTrue() : permissions;
      const payload = {
        email: trimEmail,
        display_name: trimName,
        role,
        is_active: true,
        permissions: perms,
        jobber_employee_name: jobberEmployee || null,
        invited_by: inviterEmail,
        invited_at: new Date().toISOString(),
      };

      if (inviteType === "email") {
        // Insert crew member, get their ID, then send Supabase email invite
        const { data: inserted, error: dbErr } = await supabase
          .from("crew_members")
          .insert(payload)
          .select("id")
          .single();
        if (dbErr) {
          const isDuplicate = dbErr.code === "23505" || (dbErr.message || "").includes("duplicate key");
          if (isDuplicate) {
            setPendingUpdate(payload);
            setSaving(false);
            return;
          }
          throw dbErr;
        }
        if (!sendInviteNow) {
          onSaved("silent");
          return;
        }
        const result = await sendApiInvite(trimEmail, trimName, role, inserted.id);
        if (result?.emailSent === false && result?.inviteLink) {
          setManualLink(result.inviteLink);
          setError(`Account created, but email send failed: ${result.emailError || "unknown error"}. Copy the link below and send it manually.`);
        } else {
          onSaved("email");
        }
      } else {
        // Google OAuth invite — existing behavior
        const { error: dbErr } = await supabase.from("crew_members").insert(payload);
        if (dbErr) {
          const isDuplicate = dbErr.code === "23505" || (dbErr.message || "").includes("duplicate key");
          if (isDuplicate) {
            setPendingUpdate(payload);
            setSaving(false);
            return;
          }
          throw dbErr;
        }
        if (!sendInviteNow) {
          onSaved("silent");
          return;
        }
        await sendApiInvite(trimEmail, trimName, role, null);
        onSaved("google");
      }
    } catch (err) {
      setError(err.message || "Failed to invite member.");
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateExisting() {
    if (!pendingUpdate) return;
    setSaving(true);
    setError(null);
    try {
      const { data: updated, error: dbErr } = await supabase
        .from("crew_members")
        .update(pendingUpdate)
        .eq("email", pendingUpdate.email)
        .select("id")
        .single();
      if (dbErr) throw dbErr;
      if (!sendInviteNow) {
        onSaved("silent");
        return;
      }
      const result = await sendApiInvite(pendingUpdate.email, pendingUpdate.display_name, pendingUpdate.role, inviteType === "email" ? updated.id : null);
      if (result?.emailSent === false && result?.inviteLink) {
        setManualLink(result.inviteLink);
        setError(`Member updated, but email send failed: ${result.emailError || "unknown error"}. Copy the link below and send it manually.`);
      } else {
        onSaved(inviteType);
      }
    } catch (err) {
      setError(err.message || "Failed to update member.");
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = {
    width: "100%", padding: "10px 12px", borderRadius: 8,
    border: `1px solid ${C.border}`, background: C.card,
    color: C.text, fontSize: 14, fontFamily: "'Roboto', sans-serif",
    outline: "none",
  };

  const selectStyle = {
    ...inputStyle, cursor: "pointer",
  };

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.accent}40`,
      borderRadius: 14, padding: 24, marginBottom: 24,
    }}>
      <div style={{
        fontSize: 18, fontFamily: "'Bebas Neue', sans-serif",
        letterSpacing: "0.04em", color: C.accent, marginBottom: 20
      }}>
        Invite Crew Member
      </div>

      {/* Invite type toggle */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Sign-In Method
        </label>
        <div style={{ display: "flex", gap: 6 }}>
          {[["google", "Google OAuth"], ["email", "Email + Password"]].map(([val, label]) => (
            <button
              key={val}
              type="button"
              onClick={() => handleInviteTypeChange(val)}
              style={{
                padding: "7px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: "pointer", fontFamily: "'Roboto', sans-serif",
                border: `1px solid ${inviteType === val ? C.accent : C.border}`,
                background: inviteType === val ? C.accentDim : "transparent",
                color: inviteType === val ? C.accent : C.muted,
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>
          {inviteType === "email"
            ? "Sends a one-time link they click to set a password. Works for any email."
            : "They sign in with a Google account at that email address."}
        </div>
      </div>

      {/* For email invites: option to link to existing crew member */}
      {inviteType === "email" && (
        <div style={{ marginBottom: 18 }}>
          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Link to Existing Crew Member
          </label>
          <select
            value={linkedMemberId}
            onChange={e => setLinkedMemberId(e.target.value)}
            style={selectStyle}
          >
            <option value="">— Create New Crew Member —</option>
            {(allMembers || []).filter(m => m.role !== "owner").map(m => (
              <option key={m.id} value={m.id}>
                {m.display_name || m.email} ({m.role})
              </option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>
            Link a test email to an existing member so it logs in as them (same permissions).
          </div>
        </div>
      )}

      {/* Pick from the Jobber crew — auto-fills email + name below */}
      {!isLinkingExisting && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Crew Member (from Jobber)
          </label>
          <select value={jobberEmployee} onChange={e => handleJobberEmployeeChange(e.target.value)} style={selectStyle}>
            <option value="">— Select crew member (or fill in manually) —</option>
            {jobberEmployees.map(name => {
              const takenBy = existingMappings?.[name];
              const disabled = takenBy && takenBy !== email.toLowerCase();
              return (
                <option key={name} value={name} disabled={disabled}>
                  {name}{disabled ? ` (used by ${takenBy})` : ""}
                </option>
              );
            })}
          </select>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>
            Auto-fills their email and name from Jobber. Bonus Tracker auto-scopes to this name for non-admins.
          </div>
        </div>
      )}

      {/* Invite email */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {inviteType === "email" ? "Invite Email *" : "Email *"}
        </label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="anyone@example.com"
          style={inputStyle}
        />
        {inviteType === "email" && (
          <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>
            This is where the invite link will be sent (can be any email, doesn't need to be in Google).
          </div>
        )}
      </div>

      {/* New crew member fields — hidden when linking to existing */}
      {!isLinkingExisting && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Display Name *
              </label>
              <input
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="First Last"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Role *
              </label>
              <select value={role} onChange={e => handleRoleChange(e.target.value)} style={selectStyle}>
                <option value="admin">Admin</option>
                <option value="crew">Crew</option>
              </select>
            </div>
          </div>

          {role === "admin" && (
            <div style={{
              padding: "10px 14px", borderRadius: 8, marginBottom: 16,
              background: C.blueDim, border: `1px solid ${C.blue}30`,
              fontSize: 12, color: C.blue
            }}>
              Admin role gets all permissions automatically.
            </div>
          )}

          {role === "crew" && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
                Select what this crew member can see:
              </div>
              <PermissionGrid value={permissions} onChange={setPermissions} disabled={false} />
            </div>
          )}
        </>
      )}

      {pendingUpdate && (
        <div style={{
          padding: "12px 14px", borderRadius: 8, marginBottom: 16,
          background: C.yellowDim, border: `1px solid ${C.yellow}40`,
          fontSize: 13, color: C.yellow
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            {pendingUpdate.email} is already in the crew list.
          </div>
          <div style={{ marginBottom: 12, color: C.text, fontSize: 12 }}>
            Update their existing record with these settings (role, permissions, active status) instead?
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={handleUpdateExisting}
              disabled={saving}
              style={{
                padding: "7px 18px", borderRadius: 8, border: "none",
                background: saving ? `${C.yellow}60` : C.yellow,
                color: "#0d0f12", fontSize: 12, fontWeight: 700,
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Updating..." : "Yes, Update Existing"}
            </button>
            <button
              onClick={() => setPendingUpdate(null)}
              style={{
                padding: "7px 14px", borderRadius: 8,
                border: `1px solid ${C.border}`, background: "transparent",
                color: C.muted, fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div style={{
          padding: "10px 14px", borderRadius: 8, marginBottom: 16,
          background: C.redDim, border: `1px solid ${C.red}30`,
          fontSize: 13, color: C.red
        }}>
          {error}
        </div>
      )}

      {manualLink && (
        <div style={{
          padding: "12px 14px", borderRadius: 8, marginBottom: 16,
          background: C.accentDim, border: `1px solid ${C.accent}40`,
          fontSize: 13, color: C.text
        }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
            Invite Link — text or share manually
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              readOnly
              value={manualLink}
              onFocus={e => e.target.select()}
              style={{
                flex: 1, padding: "8px 10px", borderRadius: 6,
                border: `1px solid ${C.border}`, background: C.card,
                color: C.text, fontSize: 12, fontFamily: "monospace"
              }}
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(manualLink).then(
                  () => { setManualLinkCopied(true); setTimeout(() => setManualLinkCopied(false), 2000); },
                  () => setManualLinkCopied(false)
                );
              }}
              style={{
                padding: "8px 14px", borderRadius: 6, border: `1px solid ${C.accent}`,
                background: C.accent, color: C.bg, fontSize: 12, fontWeight: 700,
                cursor: "pointer", fontFamily: "'Roboto', sans-serif", whiteSpace: "nowrap"
              }}
            >
              {manualLinkCopied ? "Copied!" : "Copy Link"}
            </button>
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
            Link expires in 24 hours. Send it via text/iMessage/Slack.
          </div>
        </div>
      )}

      {/* Send-now toggle — untick to add silently, View As, then send from the row */}
      {!isLinkingExisting && (
        <label style={{
          display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 16,
          cursor: "pointer",
        }}>
          <input
            type="checkbox"
            checked={sendInviteNow}
            onChange={e => setSendInviteNow(e.target.checked)}
            style={{ accentColor: C.accent, width: 14, height: 14, marginTop: 2 }}
          />
          <span style={{ flex: 1 }}>
            <span style={{ fontSize: 13, color: C.text }}>Email the invite now</span>
            <span style={{ display: "block", fontSize: 11, color: C.dim, marginTop: 2, lineHeight: 1.4 }}>
              Untick to add them without sending anything — you can then View As them to
              check what they'd see, and hit "Send Email Invite" on their row when ready.
            </span>
          </span>
        </label>
      )}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button
          onClick={onCancel}
          style={{
            padding: "9px 20px", borderRadius: 8, border: `1px solid ${C.border}`,
            background: "transparent", color: C.muted, fontSize: 13,
            fontWeight: 600, cursor: "pointer", fontFamily: "'Roboto', sans-serif"
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !!pendingUpdate}
          style={{
            padding: "9px 24px", borderRadius: 8, border: "none",
            background: (saving || pendingUpdate) ? `${C.accent}60` : C.accent,
            color: "#0d0f12", fontSize: 13, fontWeight: 700,
            cursor: (saving || pendingUpdate) ? "not-allowed" : "pointer",
            fontFamily: "'Roboto', sans-serif"
          }}
        >
          {saving
            ? "Saving..."
            : !sendInviteNow && !isLinkingExisting
              ? "Add Member (No Invite)"
              : (inviteType === "email" ? "Send Email Invite" : "Add Member")}
        </button>
      </div>
    </div>
  );
}

// ── Member row (expandable) ──────────────────────────────────────────────────
function MemberRow({ member, isExpanded, existingMappings, jobberEmployees, canViewAs, onViewAs, onToggleExpand, onUpdated, onDelete, authToken }) {
  const [permissions, setPermissions] = useState(member.permissions || {});
  const [jobberEmployee, setJobberEmployee] = useState(member.jobber_employee_name || "");
  const [memberRole, setMemberRole] = useState(member.role || "crew");
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [msg, setMsg] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copyingLink, setCopyingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);

  // Email the branded invite (set-password link) for an existing row — pairs
  // with the "add without sending" option on the invite form.
  async function handleSendInvite() {
    setSendingInvite(true);
    setMsg(null);
    try {
      const resp = await fetch("/api/send-invite", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { "Authorization": `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          email: member.email,
          displayName: member.display_name || null,
          role: member.role || null,
          inviteType: "email",
          crewMemberId: member.id,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || `Invite failed (${resp.status})`);
      if (data?.emailSent === false) {
        throw new Error(`Email send failed${data?.emailError ? `: ${data.emailError}` : ""} — use Copy Invite Link and text it instead.`);
      }
      setInviteSent(true);
      setTimeout(() => setInviteSent(false), 3000);
    } catch (err) {
      setMsg({ ok: false, text: err.message || "Could not send invite." });
    } finally {
      setSendingInvite(false);
    }
  }

  async function handleCopyInviteLink() {
    setCopyingLink(true);
    setMsg(null);
    try {
      const resp = await fetch("/api/send-invite", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { "Authorization": `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          email: member.email,
          crewMemberId: member.id,
          inviteType: "email",
          linkOnly: true,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.inviteLink) {
        throw new Error(data?.error || `Failed to generate link (${resp.status})`);
      }
      await navigator.clipboard.writeText(data.inviteLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch (err) {
      setMsg({ ok: false, text: err.message || "Could not copy invite link." });
    } finally {
      setCopyingLink(false);
    }
  }

  // Reset local state when member changes externally
  useEffect(() => {
    setPermissions(member.permissions || {});
    setJobberEmployee(member.jobber_employee_name || "");
    setMemberRole(member.role || "crew");
  }, [member.permissions, member.jobber_employee_name, member.role]);

  function handleRoleChange(newRole) {
    setMemberRole(newRole);
    if (newRole === "admin") setPermissions(allTrue());
  }

  async function handleSavePermissions() {
    setSaving(true);
    setMsg(null);
    try {
      const effectivePerms = memberRole === "admin" ? allTrue() : permissions;
      const { error } = await supabase
        .from("crew_members")
        .update({
          role: memberRole,
          permissions: effectivePerms,
          jobber_employee_name: jobberEmployee || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", member.id);
      if (error) throw error;
      setMsg({ ok: true, text: "Saved." });
      onUpdated();
    } catch (err) {
      setMsg({ ok: false, text: err.message || "Save failed." });
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveMapping() {
    setSaving(true);
    setMsg(null);
    try {
      const { error } = await supabase
        .from("crew_members")
        .update({
          jobber_employee_name: jobberEmployee || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", member.id);
      if (error) throw error;
      setMsg({ ok: true, text: "Mapping saved." });
      onUpdated();
    } catch (err) {
      setMsg({ ok: false, text: err.message || "Save failed." });
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive() {
    setToggling(true);
    setMsg(null);
    try {
      const { error } = await supabase
        .from("crew_members")
        .update({ is_active: !member.is_active, updated_at: new Date().toISOString() })
        .eq("id", member.id);
      if (error) throw error;
      onUpdated();
    } catch (err) {
      setMsg({ ok: false, text: err.message || "Update failed." });
    } finally {
      setToggling(false);
    }
  }

  const isOwnerRow = member.role === "owner";
  const isAdminOrOwner = memberRole === "owner" || memberRole === "admin";

  return (
    <div style={{
      border: `1px solid ${isExpanded ? C.accent + "50" : C.border}`,
      borderRadius: 12, overflow: "hidden",
      background: isExpanded ? C.card : "transparent",
      transition: "border-color 0.15s",
    }}>
      {/* Row header */}
      <div
        onClick={() => !isOwnerRow && onToggleExpand(member.id)}
        style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "14px 18px",
          cursor: isOwnerRow ? "default" : "pointer",
        }}
      >
        {/* Avatar */}
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: C.accentDim, border: `1px solid ${C.accent}40`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 15, fontWeight: 700, color: C.accent, flexShrink: 0,
        }}>
          {(member.display_name || member.email || "?")[0].toUpperCase()}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: C.text }}>
              {member.display_name || member.email}
            </span>
            <RoleBadge role={member.role} />
            {!member.is_active && (
              <span style={{
                fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: "0.06em", padding: "2px 6px", borderRadius: 4,
                background: C.redDim, color: C.red
              }}>Inactive</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: C.muted }}>{member.email}</div>
        </div>

        {/* Last login */}
        <div style={{ fontSize: 11, color: C.dim, textAlign: "right", flexShrink: 0 }}>
          {member.last_login_at
            ? new Date(member.last_login_at).toLocaleDateString("en-CA")
            : "Never"}
        </div>

        {/* Expand chevron */}
        {!isOwnerRow && (
          <div style={{
            fontSize: 16, color: C.muted, flexShrink: 0,
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s"
          }}>
            ▾
          </div>
        )}
      </div>

      {/* Expanded editing panel */}
      {isExpanded && (
        <div style={{
          borderTop: `1px solid ${C.border}`,
          padding: "20px 18px", background: C.surface,
        }}>

          {/* Jobber employee mapping — shown for everyone including admin/owner */}
          <div style={{ marginBottom: 18 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: C.accent,
              textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8
            }}>
              Jobber Employee Mapping
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <select
                value={jobberEmployee}
                onChange={e => setJobberEmployee(e.target.value)}
                style={{
                  padding: "8px 12px", borderRadius: 8,
                  border: `1px solid ${C.border}`, background: C.card,
                  color: C.text, fontSize: 13, cursor: "pointer",
                  minWidth: 200,
                }}
              >
                <option value="">— None / Unmapped —</option>
                {jobberEmployees.map(name => {
                  const takenBy = existingMappings?.[name];
                  const disabled = takenBy && takenBy !== member.email;
                  return (
                    <option key={name} value={name} disabled={disabled}>
                      {name}{disabled ? ` (used by ${takenBy})` : ""}
                    </option>
                  );
                })}
              </select>
              {jobberEmployee !== (member.jobber_employee_name || "") && (
                <button
                  onClick={handleSaveMapping}
                  disabled={saving}
                  style={{
                    padding: "8px 16px", borderRadius: 8, border: "none",
                    background: saving ? `${C.accent}60` : C.accent,
                    color: "#0d0f12", fontSize: 12, fontWeight: 700,
                    cursor: saving ? "not-allowed" : "pointer",
                  }}
                >
                  {saving ? "Saving..." : "Save Mapping"}
                </button>
              )}
            </div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>
              Non-admins with this mapping will only see their own row in the Bonus Tracker.
            </div>
          </div>

          {/* Role selector — not shown for owner rows */}
          {!isOwnerRow && (
            <div style={{ marginBottom: 18 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: C.accent,
                textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8
              }}>
                Role
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <select
                  value={memberRole}
                  onChange={e => handleRoleChange(e.target.value)}
                  style={{
                    padding: "8px 12px", borderRadius: 8,
                    border: `1px solid ${C.border}`, background: C.card,
                    color: C.text, fontSize: 13, cursor: "pointer",
                    minWidth: 140,
                  }}
                >
                  <option value="admin">Admin</option>
                  <option value="crew">Crew</option>
                </select>
                {memberRole !== member.role && (
                  <span style={{ fontSize: 11, color: C.yellow }}>
                    Role changed — save below to apply
                  </span>
                )}
              </div>
            </div>
          )}

          {isAdminOrOwner ? (
            <div style={{
              padding: "10px 14px", borderRadius: 8, marginBottom: 16,
              background: C.blueDim, border: `1px solid ${C.blue}30`,
              fontSize: 12, color: C.blue
            }}>
              Admin/Owner accounts have all permissions.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
                Edit permissions for {member.display_name || member.email}:
              </div>
              <PermissionGrid
                value={permissions}
                onChange={setPermissions}
                disabled={false}
              />
            </>
          )}

          {/* Actions */}
          <div style={{
            display: "flex", gap: 10, alignItems: "center",
            marginTop: 18, flexWrap: "wrap"
          }}>
            {!isAdminOrOwner && (
              <button
                onClick={handleSavePermissions}
                disabled={saving}
                style={{
                  padding: "9px 20px", borderRadius: 8, border: "none",
                  background: saving ? `${C.accent}60` : C.accent,
                  color: "#0d0f12", fontSize: 13, fontWeight: 700,
                  cursor: saving ? "not-allowed" : "pointer",
                  fontFamily: "'Roboto', sans-serif"
                }}
              >
                {saving ? "Saving..." : "Save Permissions"}
              </button>
            )}

            {canViewAs && !isOwnerRow && (
              <button
                onClick={() => onViewAs(member.email)}
                style={{
                  padding: "9px 18px", borderRadius: 8,
                  border: `1px solid ${C.accent}60`,
                  background: "transparent", color: C.accent,
                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                  fontFamily: "'Roboto', sans-serif"
                }}
              >
                View As
              </button>
            )}

            {!isOwnerRow && (
              <button
                onClick={handleSendInvite}
                disabled={sendingInvite}
                style={{
                  padding: "9px 18px", borderRadius: 8,
                  border: `1px solid ${inviteSent ? C.green + "80" : C.border}`,
                  background: inviteSent ? C.greenDim : "transparent",
                  color: inviteSent ? C.green : C.text,
                  fontSize: 13, fontWeight: 600,
                  cursor: sendingInvite ? "not-allowed" : "pointer",
                  fontFamily: "'Roboto', sans-serif",
                  opacity: sendingInvite ? 0.6 : 1,
                }}
              >
                {sendingInvite ? "Sending..." : inviteSent ? "Invite Sent!" : "Send Email Invite"}
              </button>
            )}

            {!isOwnerRow && (
              <button
                onClick={handleCopyInviteLink}
                disabled={copyingLink}
                style={{
                  padding: "9px 18px", borderRadius: 8,
                  border: `1px solid ${linkCopied ? C.green + "80" : C.border}`,
                  background: linkCopied ? C.greenDim : "transparent",
                  color: linkCopied ? C.green : C.text,
                  fontSize: 13, fontWeight: 600,
                  cursor: copyingLink ? "not-allowed" : "pointer",
                  fontFamily: "'Roboto', sans-serif",
                  opacity: copyingLink ? 0.6 : 1,
                }}
              >
                {copyingLink ? "Generating..." : linkCopied ? "Link Copied!" : "Copy Invite Link"}
              </button>
            )}

            <button
              onClick={handleToggleActive}
              disabled={toggling}
              style={{
                padding: "9px 18px", borderRadius: 8,
                border: `1px solid ${member.is_active ? C.red + "60" : C.green + "60"}`,
                background: member.is_active ? C.redDim : C.greenDim,
                color: member.is_active ? C.red : C.green,
                fontSize: 13, fontWeight: 600,
                cursor: toggling ? "not-allowed" : "pointer",
                fontFamily: "'Roboto', sans-serif",
                opacity: toggling ? 0.6 : 1
              }}
            >
              {toggling
                ? "Updating..."
                : member.is_active ? "Deactivate" : "Reactivate"}
            </button>

            {!isOwnerRow && onDelete && !confirmDelete && (
              <button
                onClick={() => setConfirmDelete(true)}
                style={{
                  padding: "9px 18px", borderRadius: 8,
                  border: `1px solid ${C.dim}`,
                  background: "transparent", color: C.dim,
                  fontSize: 13, fontWeight: 600, cursor: "pointer",
                  fontFamily: "'Roboto', sans-serif",
                }}
              >
                Delete
              </button>
            )}

            {confirmDelete && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, color: C.red, fontWeight: 600 }}>
                  Remove from crew_members? They can be re-invited cleanly.
                </span>
                <button
                  onClick={() => { setConfirmDelete(false); onDelete(member.id); }}
                  style={{
                    padding: "6px 14px", borderRadius: 8, border: "none",
                    background: C.red, color: "#fff",
                    fontSize: 12, fontWeight: 700, cursor: "pointer",
                  }}
                >
                  Yes, Delete
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  style={{
                    padding: "6px 12px", borderRadius: 8,
                    border: `1px solid ${C.border}`, background: "transparent",
                    color: C.muted, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            )}

            {msg && (
              <span style={{ fontSize: 12, color: msg.ok ? C.green : C.red }}>
                {msg.text}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function ManageCrew() {
  const { realIsOwner, setViewAsEmail } = usePermissions();
  const { session } = useSession();
  const inviterEmail = session?.user?.email?.toLowerCase() || "";

  const [members, setMembers] = useState([]);
  const [jobberUsers, setJobberUsers] = useState([]); // [{id, name, is_active}] synced from Jobber
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [globalMsg, setGlobalMsg] = useState(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    const [membersRes, usersRes] = await Promise.all([
      supabase
        .from("crew_members")
        .select("*")
        .order("role", { ascending: true })
        .order("display_name", { ascending: true }),
      // Live Jobber roster — powers the "Jobber Employee" link dropdown so new
      // hires synced from Jobber appear without a code change. email feeds the
      // invite form's auto-fill.
      supabase.from("jobber_users").select("id,name,email,is_active"),
    ]);
    if (!membersRes.error) setMembers(membersRes.data || []);
    setJobberUsers(usersRes?.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  // Live "Jobber Employee" roster for the mapping dropdowns: the hardcoded
  // fallback (for order + first render), plus any active jobber_users not
  // already covered, plus any name already saved on a crew member so it stays
  // selectable even if that Jobber user was later archived.
  const jobberEmployees = useMemo(() => {
    const knownFirsts = new Set(JOBBER_EMPLOYEES_FALLBACK.map(firstName));
    const list = [...JOBBER_EMPLOYEES_FALLBACK];
    const add = (display) => {
      const f = firstName(display);
      if (!f || knownFirsts.has(f)) return;
      knownFirsts.add(f);
      list.push(display);
    };
    for (const u of jobberUsers) {
      if (u?.is_active === false) continue;
      const f = firstName(u?.name || "");
      if (f) add(f.charAt(0).toUpperCase() + f.slice(1));
    }
    for (const m of members) {
      if (m.jobber_employee_name) add(m.jobber_employee_name);
    }
    return list;
  }, [jobberUsers, members]);

  // Only real owners may manage crew — guard placed after all hooks.
  // Use realIsOwner so an owner in "View As X" mode still reaches this page.
  if (!realIsOwner) {
    return <Navigate to="/" replace />;
  }

  // Map jobber_employee_name -> member email (used to disable already-taken options)
  const existingMappings = {};
  for (const m of members) {
    if (m.jobber_employee_name && m.is_active) {
      existingMappings[m.jobber_employee_name] = m.email;
    }
  }

  function handleToggleExpand(id) {
    setExpandedId(prev => (prev === id ? null : id));
  }

  function handleViewAs(email) {
    setViewAsEmail(email);
  }

  async function handleInviteSaved(inviteType) {
    setShowInvite(false);
    const msg = inviteType === "silent"
      ? "Member added — no invite sent yet. Expand their row to View As them, then hit Send Email Invite when ready."
      : inviteType === "email"
        ? "Invite sent! They'll receive an email with a link to set their password and sign in."
        : "Member added. An invite email was sent — they can sign in with their Google account.";
    setGlobalMsg({ ok: true, text: msg });
    await fetchMembers();
    setTimeout(() => setGlobalMsg(null), 6000);
  }

  async function handleDelete(memberId) {
    const { error } = await supabase.from("crew_members").delete().eq("id", memberId);
    if (!error) {
      setExpandedId(null);
      await fetchMembers();
      setGlobalMsg({ ok: true, text: "Member deleted. They can now be re-invited cleanly." });
      setTimeout(() => setGlobalMsg(null), 5000);
    } else {
      setGlobalMsg({ ok: false, text: error.message || "Delete failed." });
    }
  }

  const activeCount = members.filter(m => m.is_active).length;
  const totalCount = members.length;

  return (
    <div style={{
      background: C.bg, minHeight: "100vh", color: C.text,
      fontFamily: "'Roboto', sans-serif", padding: "28px 24px 60px",
      maxWidth: 860, margin: "0 auto"
    }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{
          fontSize: 36, fontFamily: "'Bebas Neue', sans-serif",
          letterSpacing: "0.04em", color: C.accent, margin: 0, lineHeight: 1
        }}>
          Manage Crew
        </h1>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>
          {activeCount} active · {totalCount} total
        </div>
      </div>

      {/* Global message */}
      {globalMsg && (
        <div style={{
          padding: "12px 16px", borderRadius: 10, marginBottom: 20,
          background: globalMsg.ok ? C.greenDim : C.redDim,
          border: `1px solid ${globalMsg.ok ? C.green : C.red}30`,
          color: globalMsg.ok ? C.green : C.red, fontSize: 13,
        }}>
          {globalMsg.text}
        </div>
      )}

      {/* Invite form or button */}
      {showInvite ? (
        <InviteForm
          inviterEmail={inviterEmail}
          authToken={session?.access_token}
          existingMappings={existingMappings}
          allMembers={members}
          jobberEmployees={jobberEmployees}
          jobberUsers={jobberUsers}
          onSaved={handleInviteSaved}
          onCancel={() => setShowInvite(false)}
        />
      ) : (
        <div style={{ marginBottom: 24 }}>
          <button
            onClick={() => setShowInvite(true)}
            style={{
              padding: "10px 22px", borderRadius: 8, border: "none",
              background: C.accent, color: "#0d0f12",
              fontSize: 14, fontWeight: 700, cursor: "pointer",
              fontFamily: "'Roboto', sans-serif",
              display: "flex", alignItems: "center", gap: 8
            }}
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
            Invite Crew Member
          </button>
        </div>
      )}

      {/* Members list */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: C.muted }}>Loading...</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {members.map(member => (
            <MemberRow
              key={member.id}
              member={member}
              isExpanded={expandedId === member.id}
              existingMappings={existingMappings}
              jobberEmployees={jobberEmployees}
              canViewAs={realIsOwner}
              onViewAs={handleViewAs}
              onToggleExpand={handleToggleExpand}
              onUpdated={fetchMembers}
              onDelete={handleDelete}
              authToken={session?.access_token}
            />
          ))}
          {members.length === 0 && (
            <div style={{ textAlign: "center", padding: 48, color: C.muted }}>
              No crew members found.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
